import React, { useState, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { connectRefinementList, connectStateResults } from 'react-instantsearch-dom';
import { trackAlgoliaClickedFilter, trackFacetClick } from 'ck-utils/algoliaAnalytics';
import { CheckBox, Icon, Typography } from 'ck-ui';
import { generateFacetCombinations } from 'ck-utils';
import { Label } from '../utils';
import { FacetContainer } from './FacetContainer';
import { ProductListingPageContext } from '../context';

const CheckboxList = ({
  attribute,
  label,
  items: originalItems,
  refine,
  currentRefinement,
  searchResults,
  hasFacetSearch,
  isFromSearch,
  searchForItems,
  sort,
  filter,
  localeKey,
}) => {
  const { searchState } = useContext(ProductListingPageContext);
  const [showMore, setShowMore] = useState(false);
  const searchInputRef = useRef();
  let items = originalItems;
  if (filter) items = items.filter(filter);
  if (sort) items = items.sort(sort);

  const hasEnoughToShowMore = hasFacetSearch && items.length > 9;

  const clearSearch = () => {
    if (searchInputRef) {
      searchInputRef.current.value = '';
    }
    searchForItems('');
  };

  return (
    (items.length < 1 && !(window.Shopify.toggleExtraFilters?.toLowerCase()?.includes(label))) && !isFromSearch ? null : (
      <FacetContainer
        refinedCount={currentRefinement.length}
        label={label}
        render={() => (
          <>
            {hasFacetSearch && (
              <div className="facet-search-container">
                <input
                  type="text"
                  className="facet-search"
                  placeholder={`Search ${label}..`}
                  ref={searchInputRef}
                  onChange={e => searchForItems(e.target.value)}
                />
                <button type="button" onClick={clearSearch}>
                  <Icon name="cross" color="grey-dark" />
                </button>
              </div>
            )}
            {(hasEnoughToShowMore && !showMore ? items.slice(0, 9) : items).map(item => (
              <label key={item.label} className="facet-label">
                <CheckBox
                  checked={item.isRefined}
                  onChange={() => {
                    if (!item.isRefined) {
                      trackAlgoliaClickedFilter({
                        index: searchResults?.index,
                        filters: [`${attribute}:${item.label}`]
                      });
                      trackFacetClick({
                        facet_type: 'left_menu',
                        facet_name: attribute,
                        facet_value: item.value,
                        facet_combinations: generateFacetCombinations(searchState, attribute)
                      });
                    }
                    refine(item.value);
                  }}
                >
                  <span className="is-capitalized">
                    {localeKey ? Label.getLocaleString(item.label, localeKey) : item.label}
                  </span>
                </CheckBox>
                {' '}
                <Typography display="inline">({item.count})</Typography>
              </label>
            ))}
            {hasEnoughToShowMore && (
              <span
                role="button"
                tabIndex="0"
                onClick={() => setShowMore(!showMore)}
                className="facet-search-more-wrapper"
              >
                <Icon name={showMore ? 'minus' : 'plus'} size="small" />
                <span className="facet-search-more">{showMore ? 'Less' : 'More'}</span>
              </span>
            )}
          </>
        )}
      />
    ));
};

CheckboxList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  currentRefinement: PropTypes.arrayOf(PropTypes.string).isRequired,
  refine: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  attribute: PropTypes.string.isRequired,
  searchResults: PropTypes.object,
  hasFacetSearch: PropTypes.bool,
  isFromSearch: PropTypes.bool.isRequired,
  searchForItems: PropTypes.func.isRequired,
  sort: PropTypes.func,
  filter: PropTypes.func,
  localeKey: PropTypes.string,
};

CheckboxList.defaultProps = {
  searchResults: null,
  hasFacetSearch: false,
  sort: null,
  filter: null,
  localeKey: null,
};

export const CheckboxListFacet = connectRefinementList(connectStateResults(CheckboxList));
